import React, { useState, useEffect } from 'react';
import IconProvider from '../../../../ui/IconProvider';
import { FolderItem, SelectedEntity } from './FolderStructure';
import { ItemType } from './FolderStructure/types';
import { SidebarMenu, SidebarMenuItem, SidebarMenuSub, SidebarMenuSubItem } from '@/components/layout/side-bars/sidebar';
import FolderMenuItem from './FolderMenuItem';

interface FolderTreeProps {
  folders: FolderItem[];
  selectedFolderId?: string | null;
  onFolderSelect: (folderId: string | null) => void;
  onFolderCreate?: (parentId?: string) => void;
  onFolderRename?: (folderId: string, newName: string) => void;
  onFolderDelete?: (folderId: string) => void;
  onFolderColorChange?: (folderId: string, color: string | null) => void;
  onItemDrop?: (itemId: string, folderId: string | null) => void;
  onFolderMove?: (folderId: string, newParentId: string | null) => void;
  showItemCounts?: boolean;
  allowCreation?: boolean;
  allowRename?: boolean;
  allowDelete?: boolean;
  enableDragDrop?: boolean;
  chevronSize?: number;
  showFolderIcon?: boolean;
  folderIconSize?: number;
  isLoading?: boolean;
  rootItemCount?: number;
  rootFolderWithItems?: FolderItem | null;
  // Item handling props
  showItems?: boolean;
  onItemSelect?: (item: any) => void;
  selectedItemId?: string | null;
  // Item type and creation props
  itemType?: ItemType;
  oncreateObject?: (folderId?: string) => void;
  onCreateDocument?: (folderId?: string) => void;
  onCreateBlueprint?: (folderId?: string) => void;
  onCreateDialog?: (folderId?: string) => void;
  onCreateFlow?: (folderId?: string) => void;
  onCreateVariable?: (folderId?: string) => void;
  // Item actions
  onItemRename?: (itemId: string, newName: string) => void;
  onItemDelete?: (itemId: string) => void;
  onItemDuplicate?: (itemId: string) => void;
  // Multi-select props
  selectedEntities?: Set<string>;
  onEntityClick?: (entity: SelectedEntity, event?: React.MouseEvent) => void;
  onBatchMove?: (targetFolderId: string | null) => void;
  // Auto-edit props
  newlyCreatedFolderId?: string | null;
  onClearNewlyCreatedFolder?: () => void;
}

export default function FolderTree({
  folders,
  selectedFolderId,
  onFolderSelect,
  onFolderCreate,
  onFolderRename,
  onFolderDelete,
  onFolderColorChange,
  onItemDrop,
  onFolderMove,
  showItemCounts = true,
  allowCreation = true,
  allowRename = true,
  allowDelete = true,
  enableDragDrop = true,
  chevronSize = 4,
  showFolderIcon = false,
  folderIconSize = 14,
  isLoading = false,
  rootItemCount = 0,
  rootFolderWithItems = null,
  // Item handling props
  showItems = false,
  onItemSelect,
  selectedItemId,
  // Item type and creation props
  itemType = 'glossary',
  oncreateObject,
  onCreateDocument,
  onCreateBlueprint,
  onCreateDialog,
  onCreateFlow,
  onCreateVariable,
  // Item actions
  onItemRename,
  onItemDelete,
  onItemDuplicate,
  // Multi-select props
  selectedEntities = new Set(),
  onEntityClick,
  onBatchMove,
  // Auto-edit props
  newlyCreatedFolderId,
  onClearNewlyCreatedFolder,
}: FolderTreeProps) {
  // Load expanded folders from localStorage on mount
  const [expandedFolders, setExpandedFolders] = useState<Set<string>>(() => {
    try {
      const storageKey = `locanest_expanded_folders_${itemType}`;
      const stored = localStorage.getItem(storageKey);
      if (stored) {
        return new Set(JSON.parse(stored));
      }
    } catch (error) {
      console.error('Failed to load expanded folders from localStorage:', error);
    }
    return new Set();
  });
  
  const [editingFolderId, setEditingFolderId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState('');
  const [dragOverFolderId, setDragOverFolderId] = useState<string | null>(null);
  
  // Save expanded folders to localStorage whenever it changes
  useEffect(() => {
    try {
      const storageKey = `locanest_expanded_folders_${itemType}`;
      localStorage.setItem(storageKey, JSON.stringify(Array.from(expandedFolders)));
    } catch (error) {
      console.error('Failed to save expanded folders to localStorage:', error);
    }
  }, [expandedFolders, itemType]);
  
  // Helper function to find all parent folders of a given folder
  const findParentPath = (folderId: string, folderList: FolderItem[], path: string[] = []): string[] | null => {
    for (const folder of folderList) {
      if (folder.id === folderId) {
        return path;
      }
      if (folder.children && folder.children.length > 0) {
        const result = findParentPath(folderId, folder.children, [...path, folder.id]);
        if (result !== null) {
          return result;
        }
      }
    }
    return null;
  };
  
  // Function to expand a folder and all its parents
  const expandFolderAndParents = (folderId: string) => {
    setExpandedFolders(prev => {
      const newSet = new Set(prev);
      
      // Find all parent folders
      const parentPath = findParentPath(folderId, folders);
      
      // Expand all parents in the path
      if (parentPath) {
        parentPath.forEach(id => newSet.add(id));
      }
      
      // Expand the folder itself
      newSet.add(folderId);
      
      return newSet;
    });
  };
  
  // Wrapper for onFolderCreate that auto-expands parent and all ancestors
  const handleFolderCreate = (parentId?: string) => {
    if (onFolderCreate) {
      onFolderCreate(parentId);
      // Auto-expand the parent folder and all its ancestors to show the new subfolder
      if (parentId) {
        expandFolderAndParents(parentId);
      }
    }
  };

  const toggleFolder = (folderId: string) => {
    setExpandedFolders(prev => {
      const newSet = new Set(prev);
      if (newSet.has(folderId)) {
        newSet.delete(folderId);
      } else {
        newSet.add(folderId);
      }
      return newSet;
    });
  };

  const startEditing = (folder: FolderItem) => {
    setEditingFolderId(folder.id);
    setEditingName(folder.name);
  };

  const saveEdit = () => {
    if (editingFolderId && editingName.trim() && onFolderRename) {
      onFolderRename(editingFolderId, editingName.trim());
    }
    setEditingFolderId(null);
    setEditingName('');
  };

  const cancelEdit = () => {
    setEditingFolderId(null);
    setEditingName('');
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      saveEdit();
    } else if (e.key === 'Escape') {
      cancelEdit();
    }
  };

  const handleDragOver = (e: React.DragEvent, folderId: string | null) => {
    if (!enableDragDrop || !onItemDrop) return;
    
    e.preventDefault();
    e.stopPropagation();
    setDragOverFolderId(folderId);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    if (!enableDragDrop || !onItemDrop) return;
    
    e.preventDefault();
    e.stopPropagation();
    setDragOverFolderId(null);
  };

  const handleDrop = (e: React.DragEvent, folderId: string | null) => {
    if (!enableDragDrop) return;
    
    e.preventDefault();
    e.stopPropagation();
    setDragOverFolderId(null);

    try {
      // Check if we're dragging multiple selected items
      const dragDataJson = e.dataTransfer.getData("application/json");
      if (dragDataJson) {
        const dragData = JSON.parse(dragDataJson);
        
        if (dragData.isMultiSelect && onBatchMove) {
          // Handle multi-select drop
          onBatchMove(folderId);
        } else if (dragData.type === 'item' && onItemDrop) {
          // Handle single item drop
          onItemDrop(dragData.id, folderId);
        } else if (dragData.type === 'folder' && onFolderMove) {
          // Handle single folder drop
          onFolderMove(dragData.id, folderId);
        }
      } else {
        // Fallback to plain text
        const itemId = e.dataTransfer.getData('text/plain');
        if (itemId && onItemDrop) {
          onItemDrop(itemId, folderId);
        }
      }
    } catch (error) {
      console.error('Error handling drop:', error);
    }
  };

  const renderFolderItem = (folder: FolderItem, level: number = 0) => {
    const isExpanded = expandedFolders.has(folder.id);
    const hasChildren = folder.children && folder.children.length > 0;

    if (level === 0) {
      // Root level items use SidebarMenuItem
      return (
        <SidebarMenuItem key={folder.id}>
          <FolderMenuItem
            folder={folder}
            level={level}
            selectedFolderId={selectedFolderId}
            onFolderSelect={onFolderSelect}
            onFolderCreate={handleFolderCreate}
            onFolderRename={onFolderRename}
            onFolderDelete={onFolderDelete}
            onFolderColorChange={onFolderColorChange}
            onItemDrop={onItemDrop}
            onFolderMove={onFolderMove}
            showItemCounts={showItemCounts}
            allowCreation={allowCreation}
            allowRename={allowRename}
            allowDelete={allowDelete}
            enableDragDrop={enableDragDrop}
            isExpanded={isExpanded}
            onToggleExpanded={() => toggleFolder(folder.id)}
            chevronSize={chevronSize}
            showFolderIcon={folder.id === 'root' ? false : showFolderIcon}
            folderIconSize={folderIconSize}
            showItems={showItems}
            onItemSelect={onItemSelect}
            selectedItemId={selectedItemId}
            itemType={itemType}
            oncreateObject={oncreateObject}
            onCreateDocument={onCreateDocument}
            onCreateBlueprint={onCreateBlueprint}
            onCreateDialog={onCreateDialog}
            onCreateFlow={onCreateFlow}
            onCreateVariable={onCreateVariable}
            onItemRename={onItemRename}
            onItemDelete={onItemDelete}
            onItemDuplicate={onItemDuplicate}
            selectedEntities={selectedEntities}
            onEntityClick={onEntityClick}
            onBatchMove={onBatchMove}
            newlyCreatedFolderId={newlyCreatedFolderId}
            onClearNewlyCreatedFolder={onClearNewlyCreatedFolder}
          />
          
          {hasChildren && isExpanded && (
            <SidebarMenuSub>
              {folder.children!.map(child => renderFolderItem(child, level + 1))}
            </SidebarMenuSub>
          )}
        </SidebarMenuItem>
      );
    } else {
      // Sub-level items use SidebarMenuSubItem
      return (
        <SidebarMenuSubItem key={folder.id}>
          <FolderMenuItem
            folder={folder}
            level={level}
            selectedFolderId={selectedFolderId}
            onFolderSelect={onFolderSelect}
            onFolderCreate={handleFolderCreate}
            onFolderRename={onFolderRename}
            onFolderDelete={onFolderDelete}
            onFolderColorChange={onFolderColorChange}
            onItemDrop={onItemDrop}
            onFolderMove={onFolderMove}
            showItemCounts={showItemCounts}
            allowCreation={allowCreation}
            allowRename={allowRename}
            allowDelete={allowDelete}
            enableDragDrop={enableDragDrop}
            isExpanded={isExpanded}
            onToggleExpanded={() => toggleFolder(folder.id)}
            chevronSize={chevronSize}
            showFolderIcon={folder.id === 'root' ? false : showFolderIcon}
            folderIconSize={folderIconSize}
            showItems={showItems}
            onItemSelect={onItemSelect}
            selectedItemId={selectedItemId}
            itemType={itemType}
            oncreateObject={oncreateObject}
            onCreateDocument={onCreateDocument}
            onCreateBlueprint={onCreateBlueprint}
            onCreateDialog={onCreateDialog}
            onCreateFlow={onCreateFlow}
            onCreateVariable={onCreateVariable}
            onItemRename={onItemRename}
            onItemDelete={onItemDelete}
            onItemDuplicate={onItemDuplicate}
            selectedEntities={selectedEntities}
            onEntityClick={onEntityClick}
            onBatchMove={onBatchMove}
            newlyCreatedFolderId={newlyCreatedFolderId}
            onClearNewlyCreatedFolder={onClearNewlyCreatedFolder}
          />
          
          {hasChildren && isExpanded && (
            <SidebarMenuSub>
              {folder.children!.map(child => renderFolderItem(child, level + 1))}
            </SidebarMenuSub>
          )}
        </SidebarMenuSubItem>
      );
    }
  };

  return (
    <SidebarMenu>
      {/* Root items - items not in any folder - rendered directly without folder wrapper */}
      {rootFolderWithItems && rootFolderWithItems.children && rootFolderWithItems.children.length > 0 ? (
        // Render each root item directly at the root level
        <>
          {rootFolderWithItems.children.map(item => renderFolderItem(item, 0))}
        </>
      ) : rootItemCount && rootItemCount > 0 ? (
        // Show simple root items button when showItems is false but there are root items
        <SidebarMenuItem>
          <FolderMenuItem
            folder={{
              id: 'root',
              name: 'Root',
              type: 'folder',
              itemCount: rootItemCount,
            }}
            level={0}
            selectedFolderId={selectedFolderId}
            onFolderSelect={onFolderSelect}
            onFolderCreate={handleFolderCreate}
            onFolderRename={undefined} // Don't allow renaming root
            onFolderDelete={undefined} // Don't allow deleting root
            onItemDrop={onItemDrop}
            onFolderMove={onFolderMove} // Allow folders to be dropped on root to move to root level
            showItemCounts={showItemCounts}
            allowCreation={allowCreation}
            allowRename={false} // Don't allow renaming root
            allowDelete={false} // Don't allow deleting root
            enableDragDrop={enableDragDrop}
            isExpanded={false} // Root doesn't expand when no items
            onToggleExpanded={undefined}
            chevronSize={chevronSize}
            showFolderIcon={false}
            folderIconSize={folderIconSize}
            showItems={showItems}
            onItemSelect={onItemSelect}
            selectedItemId={selectedItemId}
            itemType={itemType}
            oncreateObject={oncreateObject}
            onCreateDocument={onCreateDocument}
            onCreateBlueprint={onCreateBlueprint}
            onCreateDialog={onCreateDialog}
            onCreateFlow={onCreateFlow}
            onCreateVariable={onCreateVariable}
            onItemRename={onItemRename}
            onItemDelete={onItemDelete}
            onItemDuplicate={onItemDuplicate}
            selectedEntities={selectedEntities}
            onEntityClick={onEntityClick}
            onBatchMove={onBatchMove}
            newlyCreatedFolderId={newlyCreatedFolderId}
            onClearNewlyCreatedFolder={onClearNewlyCreatedFolder}
          />
        </SidebarMenuItem>
      ) : null}

      {/* Regular folders */}
      {folders.map(folder => renderFolderItem(folder))}
    </SidebarMenu>
  );
}
